import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const TournamentResultCard = ({ tournament, onPublishResult, onSavePartial, teams, players }) => {
  const [resultData, setResultData] = useState({
    championTeamId: '',
    finalist1Id: '',
    finalist2Id: '',
    semifinalist1Id: '',
    semifinalist2Id: '',
    semifinalist3Id: '',
    semifinalist4Id: '',
    playerOfTournamentId: '',
    top4Team1Id: '',
    top4Team2Id: '',
    top4Team3Id: '',
    top4Team4Id: '',
    superOverTeamIds: []
  });

  const [showDetails, setShowDetails] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const isPublished = tournament?.tournamentResultsStatus === 'published';
  const isPartialSaved = tournament?.tournamentResultsStatus === 'partial';

  // Pre-populate form with existing data when tournament results are published or partially saved
  useEffect(() => {
    if ((isPublished || isPartialSaved) && tournament) {
      setResultData({
        championTeamId: tournament?.championTeamId || '',
        finalist1Id: tournament?.finalist1Id || '',
        finalist2Id: tournament?.finalist2Id || '',
        semifinalist1Id: tournament?.semifinalist1Id || '',
        semifinalist2Id: tournament?.semifinalist2Id || '',
        semifinalist3Id: tournament?.semifinalist3Id || '',
        semifinalist4Id: tournament?.semifinalist4Id || '',
        playerOfTournamentId: tournament?.playerOfTournamentId || '',
        top4Team1Id: tournament?.top4Team1Id || '',
        top4Team2Id: tournament?.top4Team2Id || '',
        top4Team3Id: tournament?.top4Team3Id || '',
        top4Team4Id: tournament?.top4Team4Id || '',
        superOverTeamIds: Array.isArray(tournament?.superOverTeamIds) ? tournament?.superOverTeamIds : (tournament?.superOverTeamIds ? [tournament?.superOverTeamIds] : [])
      });
    }
  }, [tournament, isPublished, isPartialSaved]);

  const handleInputChange = (field, value) => {
    setResultData(prev => ({ ...prev, [field]: value }));
  };

  const teamOptions = teams?.map(team => ({
    value: team?.id,
    label: team?.name
  }));

  const playerOptions = players?.map(player => ({
    value: player?.id,
    label: `${player?.name} (${player?.team})`
  }));

  const isFormValid = () => {
    return (
      resultData?.championTeamId &&
      resultData?.finalist1Id &&
      resultData?.finalist2Id &&
      resultData?.semifinalist1Id &&
      resultData?.semifinalist2Id &&
      resultData?.semifinalist3Id &&
      resultData?.semifinalist4Id &&
      resultData?.playerOfTournamentId
    );
  };

  const hasAnyData = () => {
    return (
      resultData?.championTeamId ||
      resultData?.finalist1Id ||
      resultData?.finalist2Id ||
      resultData?.semifinalist1Id ||
      resultData?.semifinalist2Id ||
      resultData?.semifinalist3Id ||
      resultData?.semifinalist4Id ||
      resultData?.playerOfTournamentId ||
      resultData?.top4Team1Id ||
      resultData?.top4Team2Id ||
      resultData?.top4Team3Id ||
      resultData?.top4Team4Id ||
      resultData?.superOverTeamIds?.length > 0
    );
  };

  // Calculate prediction impact preview based on filled fields
  const getPredictionImpactPreview = () => {
    const filledFields = [
      resultData?.championTeamId,
      resultData?.finalist1Id,
      resultData?.finalist2Id,
      resultData?.semifinalist1Id,
      resultData?.semifinalist2Id,
      resultData?.semifinalist3Id,
      resultData?.semifinalist4Id,
      resultData?.playerOfTournamentId,
      resultData?.top4Team1Id,
      resultData?.top4Team2Id,
      resultData?.top4Team3Id,
      resultData?.top4Team4Id,
    ]?.filter(Boolean)?.length + (resultData?.superOverTeamIds?.length > 0 ? 1 : 0);

    const categories = [];
    if (resultData?.championTeamId) categories?.push({ label: 'Champion', icon: 'Trophy', points: 20 });
    if (resultData?.finalist1Id && resultData?.finalist2Id) categories?.push({ label: 'Finalists', icon: 'Award', points: 10 });
    if (resultData?.semifinalist1Id && resultData?.semifinalist2Id && resultData?.semifinalist3Id && resultData?.semifinalist4Id) categories?.push({ label: 'Semifinalists', icon: 'Star', points: 5 });
    if (resultData?.playerOfTournamentId) categories?.push({ label: 'Player of Tournament', icon: 'User', points: 15 });
    if (resultData?.top4Team1Id || resultData?.top4Team2Id || resultData?.top4Team3Id || resultData?.top4Team4Id) categories?.push({ label: 'Top 4 Ranking', icon: 'List', points: 8 });
    if (resultData?.superOverTeamIds?.length > 0) categories?.push({ label: 'Super Over Team', icon: 'Zap', points: 12 });

    return { filledFields, categories };
  };

  const getStatusBadge = () => {
    if (isPublished) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-success/10 text-success text-xs font-medium rounded">
          <Icon name="CheckCircle" size={14} />
          Published
        </span>
      );
    }
    if (isPartialSaved) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded">
          <Icon name="Save" size={14} />
          Partially Saved
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-warning/10 text-warning text-xs font-medium rounded">
        <Icon name="Clock" size={14} />
        Pending
      </span>
    );
  };

  const getTeamName = (teamId) => {
    return teams?.find(t => t?.id === teamId)?.name || '';
  };

  const { filledFields, categories } = getPredictionImpactPreview();
  const isDisabled = isPublished && !isEditing;

  const handleSuperOverToggle = (teamId) => {
    setResultData(prev => {
      const current = prev?.superOverTeamIds || [];
      const updated = current?.includes(teamId)
        ? current?.filter(id => id !== teamId)
        : [...current, teamId];
      return { ...prev, superOverTeamIds: updated };
    });
  };

  return (
    <div className="bg-card rounded-lg border border-border">
      <div className="p-4 md:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-semibold text-foreground">
                Tournament Results
              </h3>
              {getStatusBadge()}
            </div>
            <p className="text-sm text-muted-foreground">
              {tournament?.name}
            </p>
            {isPartialSaved && !isPublished && (
              <p className="text-xs text-primary mt-1 flex items-center gap-1">
                <Icon name="Info" size={12} />
                Some results saved. Points calculated for saved fields. Complete remaining fields to publish.
              </p>
            )}
          </div>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors"
          >
            {showDetails ? (
              <>
                <Icon name="ChevronUp" size={18} />
                Hide Details
              </>
            ) : (
              <>
                <Icon name="ChevronDown" size={18} />
                Show Details
              </>
            )}
          </button>
        </div>

        {showDetails && (
          <div className="space-y-6">
            {/* Super Over Team Section - highlighted as saveable during live tournament */}
            <div className="bg-warning/5 border border-warning/20 rounded-lg p-4 overflow-visible">
              <div className="flex items-start justify-between mb-1">
                <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Icon name="Zap" size={16} className="text-warning" />
                  Super Over Team
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-warning/15 text-warning text-xs rounded font-normal">
                    <Icon name="Save" size={10} />
                    Save anytime
                  </span>
                </h4>
              </div>
              <p className="text-xs text-muted-foreground mb-3">Select the team(s) that participated in the Super Over. Can be saved while the tournament is still live.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-1">
                {teamOptions?.map(team => {
                  const isSelected = resultData?.superOverTeamIds?.includes(team?.value);
                  return (
                    <button
                      key={team?.value}
                      type="button"
                      disabled={isDisabled}
                      onClick={() => !isDisabled && handleSuperOverToggle(team?.value)}
                      className={`flex items-center gap-2 px-3 py-2 rounded border text-sm text-left transition-colors ${
                        isSelected
                          ? 'bg-warning/10 border-warning/40 text-warning font-medium' :'bg-card border-border text-foreground hover:border-warning/30 hover:bg-warning/5'
                      } ${isDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <span className={`flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center ${
                        isSelected ? 'bg-warning border-warning' : 'border-border bg-background'
                      }`}>
                        {isSelected && <Icon name="Check" size={10} className="text-white" />}
                      </span>
                      <span className="truncate">{team?.label}</span>
                    </button>
                  );
                })}
              </div>
              {resultData?.superOverTeamIds?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {resultData?.superOverTeamIds?.map(teamId => (
                    <span key={teamId} className="inline-flex items-center gap-1 px-2 py-1 bg-warning/10 text-warning border border-warning/20 rounded text-xs font-medium">
                      <Icon name="Zap" size={12} />
                      {getTeamName(teamId)}
                    </span>
                  ))}
                </div>
              )}
              {/* Quick save button for super over */}
              {!isDisabled && resultData?.superOverTeamIds?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-warning/15">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onSavePartial && onSavePartial(tournament?.id, { superOverTeamIds: resultData?.superOverTeamIds })}
                    className="text-warning border-warning/40 hover:bg-warning/10 text-xs"
                  >
                    <Icon name="Save" size={14} className="mr-1.5" />
                    Save Super Over & Calculate Points
                  </Button>
                </div>
              )}
            </div>

            {/* Champion Section */}
            <div className="bg-muted rounded-lg p-4 overflow-visible">
              <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                <Icon name="Trophy" size={16} className="text-warning" />
                Tournament Champion
              </h4>
              <Select
                value={resultData?.championTeamId}
                onChange={(value) => handleInputChange('championTeamId', value)}
                options={[
                  { value: '', label: 'Select Champion' },
                  ...teamOptions
                ]}
                className="w-full"
                disabled={isDisabled}
              />
            </div>

            {/* Finalists Section */}
            <div className="bg-muted rounded-lg p-4 overflow-visible">
              <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                <Icon name="Award" size={16} className="text-primary" />
                Finalists (2 Teams)
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground mb-2 block">Finalist 1</label>
                  <Select
                    value={resultData?.finalist1Id}
                    onChange={(value) => handleInputChange('finalist1Id', value)}
                    options={[
                      { value: '', label: 'Select Finalist 1' },
                      ...teamOptions
                    ]}
                    className="w-full"
                    disabled={isDisabled}
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-2 block">Finalist 2</label>
                  <Select
                    value={resultData?.finalist2Id}
                    onChange={(value) => handleInputChange('finalist2Id', value)}
                    options={[
                      { value: '', label: 'Select Finalist 2' },
                      ...teamOptions
                    ]}
                    className="w-full"
                    disabled={isDisabled}
                  />
                </div>
              </div>
            </div>

            {/* Semifinalists Section */}
            <div className="bg-muted rounded-lg p-4 overflow-visible">
              <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                <Icon name="Star" size={16} className="text-success" />
                Semifinalists (4 Teams)
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground mb-2 block">Semifinalist 1</label>
                  <Select
                    value={resultData?.semifinalist1Id}
                    onChange={(value) => handleInputChange('semifinalist1Id', value)}
                    options={[
                      { value: '', label: 'Select Semifinalist 1' },
                      ...teamOptions
                    ]}
                    className="w-full"
                    disabled={isDisabled}
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-2 block">Semifinalist 2</label>
                  <Select
                    value={resultData?.semifinalist2Id}
                    onChange={(value) => handleInputChange('semifinalist2Id', value)}
                    options={[
                      { value: '', label: 'Select Semifinalist 2' },
                      ...teamOptions
                    ]}
                    className="w-full"
                    disabled={isDisabled}
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-2 block">Semifinalist 3</label>
                  <Select
                    value={resultData?.semifinalist3Id}
                    onChange={(value) => handleInputChange('semifinalist3Id', value)}
                    options={[
                      { value: '', label: 'Select Semifinalist 3' },
                      ...teamOptions
                    ]}
                    className="w-full"
                    disabled={isDisabled}
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-2 block">Semifinalist 4</label>
                  <Select
                    value={resultData?.semifinalist4Id}
                    onChange={(value) => handleInputChange('semifinalist4Id', value)}
                    options={[
                      { value: '', label: 'Select Semifinalist 4' },
                      ...teamOptions
                    ]}
                    className="w-full"
                    disabled={isDisabled}
                  />
                </div>
              </div>
            </div>

            {/* Player of Tournament Section */}
            <div className="bg-muted rounded-lg p-4 overflow-visible">
              <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                <Icon name="User" size={16} className="text-error" />
                Player of the Tournament
              </h4>
              <Select
                value={resultData?.playerOfTournamentId}
                onChange={(value) => handleInputChange('playerOfTournamentId', value)}
                options={[
                  { value: '', label: 'Select Player' },
                  ...playerOptions
                ]}
                className="w-full"
                disabled={isDisabled}
              />
            </div>

            {/* Top 4 Teams Final Ranking Section */}
            <div className="bg-muted rounded-lg p-4 overflow-visible">
              <h4 className="text-sm font-medium text-foreground mb-1 flex items-center gap-2">
                <Icon name="List" size={16} className="text-primary" />
                Top 4 Teams Final Ranking
              </h4>
              <p className="text-xs text-muted-foreground mb-3">Select teams in order of their final ranking (1st to 4th place)</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground mb-2 block flex items-center gap-1">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-warning/20 text-warning text-xs font-bold">1</span>
                    1st Place
                  </label>
                  <Select
                    value={resultData?.top4Team1Id}
                    onChange={(value) => handleInputChange('top4Team1Id', value)}
                    options={[
                      { value: '', label: 'Select 1st Place Team' },
                      ...teamOptions
                    ]}
                    className="w-full"
                    disabled={isDisabled}
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-2 block flex items-center gap-1">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-muted-foreground/20 text-muted-foreground text-xs font-bold">2</span>
                    2nd Place
                  </label>
                  <Select
                    value={resultData?.top4Team2Id}
                    onChange={(value) => handleInputChange('top4Team2Id', value)}
                    options={[
                      { value: '', label: 'Select 2nd Place Team' },
                      ...teamOptions
                    ]}
                    className="w-full"
                    disabled={isDisabled}
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-2 block flex items-center gap-1">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-700/20 text-amber-700 text-xs font-bold">3</span>
                    3rd Place
                  </label>
                  <Select
                    value={resultData?.top4Team3Id}
                    onChange={(value) => handleInputChange('top4Team3Id', value)}
                    options={[
                      { value: '', label: 'Select 3rd Place Team' },
                      ...teamOptions
                    ]}
                    className="w-full"
                    disabled={isDisabled}
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-2 block flex items-center gap-1">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-muted-foreground/10 text-muted-foreground text-xs font-bold">4</span>
                    4th Place
                  </label>
                  <Select
                    value={resultData?.top4Team4Id}
                    onChange={(value) => handleInputChange('top4Team4Id', value)}
                    options={[
                      { value: '', label: 'Select 4th Place Team' },
                      ...teamOptions
                    ]}
                    className="w-full"
                    disabled={isDisabled}
                  />
                </div>
              </div>
              {/* Selected ranking preview */}
              {(resultData?.top4Team1Id || resultData?.top4Team2Id || resultData?.top4Team3Id || resultData?.top4Team4Id) && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {[
                    { id: resultData?.top4Team1Id, rank: '1st', color: 'bg-warning/10 text-warning border-warning/20' },
                    { id: resultData?.top4Team2Id, rank: '2nd', color: 'bg-muted-foreground/10 text-muted-foreground border-muted-foreground/20' },
                    { id: resultData?.top4Team3Id, rank: '3rd', color: 'bg-amber-700/10 text-amber-700 border-amber-700/20' },
                    { id: resultData?.top4Team4Id, rank: '4th', color: 'bg-muted-foreground/5 text-muted-foreground border-muted-foreground/10' }
                  ]?.filter(item => item?.id)?.map((item, idx) => (
                    <span key={idx} className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-xs font-medium ${item?.color}`}>
                      <span className="font-bold">{item?.rank}</span>
                      {getTeamName(item?.id)}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Prediction Impact Preview */}
            {filledFields > 0 && (
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                  <Icon name="TrendingUp" size={16} className="text-primary" />
                  Prediction Impact Preview
                </h4>
                <p className="text-xs text-muted-foreground mb-3">
                  Points will be awarded to users whose predictions match the results below
                </p>
                <div className="space-y-2">
                  {categories?.map((cat, idx) => (
                    <div key={idx} className="flex items-center justify-between py-1.5 px-3 bg-card rounded border border-border">
                      <div className="flex items-center gap-2">
                        <Icon name={cat?.icon} size={14} className="text-primary" />
                        <span className="text-xs text-foreground">{cat?.label}</span>
                      </div>
                      <span className="text-xs font-semibold text-success">+{cat?.points} pts</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-primary/10 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{filledFields} of 13 result fields filled</span>
                  <span className="text-xs font-medium text-primary">{categories?.reduce((sum, c) => sum + c?.points, 0)} max pts per user</span>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
              {!isPublished ? (
                <>
                  {/* Save Progress (partial) button - always available when any data is filled */}
                  {!isEditing && hasAnyData() && (
                    <Button
                      variant="outline"
                      onClick={() => onSavePartial && onSavePartial(tournament?.id, resultData)}
                      className="flex-1 sm:flex-initial"
                    >
                      <Icon name="Save" size={18} className="mr-2" />
                      Save Progress & Calculate Points
                    </Button>
                  )}
                  <Button
                    variant="default"
                    onClick={() => onPublishResult(tournament?.id, resultData, 'publish')}
                    disabled={!isFormValid()}
                    className="flex-1 sm:flex-initial"
                    title={!isFormValid() ? 'Fill all required fields (Champion, Finalists, Semifinalists, Player of Tournament) to publish' : ''}
                  >
                    <Icon name="Send" size={18} className="mr-2" />
                    Publish Results & Calculate Points
                  </Button>
                  {!isFormValid() && hasAnyData() && (
                    <p className="w-full text-xs text-muted-foreground flex items-center gap-1 mt-1">
                      <Icon name="Info" size={12} />
                      Fill Champion, Finalists, Semifinalists &amp; Player of Tournament to publish. You can save progress now.
                    </p>
                  )}
                </>
              ) : isEditing ? (
                <>
                  <Button
                    variant="default"
                    onClick={() => {
                      onPublishResult(tournament?.id, resultData, 'edit');
                      setIsEditing(false);
                    }}
                    disabled={!isFormValid()}
                    className="flex-1 sm:flex-initial"
                  >
                    <Icon name="Save" size={18} className="mr-2" />
                    Save Changes
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsEditing(false);
                      setResultData({
                        championTeamId: tournament?.championTeamId || '',
                        finalist1Id: tournament?.finalist1Id || '',
                        finalist2Id: tournament?.finalist2Id || '',
                        semifinalist1Id: tournament?.semifinalist1Id || '',
                        semifinalist2Id: tournament?.semifinalist2Id || '',
                        semifinalist3Id: tournament?.semifinalist3Id || '',
                        semifinalist4Id: tournament?.semifinalist4Id || '',
                        playerOfTournamentId: tournament?.playerOfTournamentId || '',
                        top4Team1Id: tournament?.top4Team1Id || '',
                        top4Team2Id: tournament?.top4Team2Id || '',
                        top4Team3Id: tournament?.top4Team3Id || '',
                        top4Team4Id: tournament?.top4Team4Id || '',
                        superOverTeamIds: Array.isArray(tournament?.superOverTeamIds) ? tournament?.superOverTeamIds : (tournament?.superOverTeamIds ? [tournament?.superOverTeamIds] : [])
                      });
                    }}
                    className="flex-1 sm:flex-initial"
                  >
                    <Icon name="X" size={18} className="mr-2" />
                    Cancel Edit
                  </Button>
                </>
              ) : (
                <Button
                  variant="default"
                  onClick={() => setIsEditing(true)}
                  className="flex-1 sm:flex-initial"
                >
                  <Icon name="Edit" size={18} className="mr-2" />
                  Edit Result
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TournamentResultCard;